import React, { Component } from 'react';

import { DndContext } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';

import DeleteIcon from '@mui/icons-material/Delete';
import ReorderIcon from '@mui/icons-material/Reorder';
import { Button, Card, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import { RoomRateInclusionSortableItem } from './RoomRateInclusionSortableItem';

interface Props {
  inclusions: Array<App.RoomRateInclusion>;
  inclusionsSchema: any;
  onDelete: (string) => void;
  onEdit: (inclusion: App.RoomRateInclusion) => void;
  onInclusionsOrderUpdate: (any) => void;
  onDeleteAll?: () => void;
}

interface State {
  inclusions: Array<App.RoomRateInclusion>;
}

export default class RoomRateInclusionsTable extends Component<Props, State> {
  constructor(props) {
    super(props);

    // Init reordering as a fallback for old records
    this.state = {
      inclusions: props.inclusions.map((inclusion, index) => {
        inclusion.order = inclusion.order && inclusion.order <= index ? inclusion.order : index;
        return inclusion;
      }),
    };
  }

  onDelete(item) {
    this.props.onDelete(item.id);
  }

  onEdit(item) {
    this.props.onEdit(item);
  }

  onDragEnd = ({ active, over }) => {
    if (!active || !over || active.id === over.id) {
      return;
    }

    const { inclusions } = this.state;

    const oldIndex = inclusions.findIndex((item) => item.id === active.id);
    const newIndex = inclusions.findIndex((item) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const { onInclusionsOrderUpdate } = this.props;
    const reorderedInclusions = arrayMove(inclusions, oldIndex, newIndex).filter(Boolean);

    reorderedInclusions.forEach((item, index) => {
      item.order = index;
    });

    this.setState({ inclusions: reorderedInclusions }, () => {
      onInclusionsOrderUpdate(reorderedInclusions.map((item) => item.id));
    });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.inclusions !== this.props.inclusions) {
      this.setState({ inclusions: this.props.inclusions });
    }
  }

  getUnit = (unit: string) => {
    const enums = this.props.inclusionsSchema.properties.unit.enum;
    for (const index in enums) {
      if (enums[index] === unit) {
        return this.props.inclusionsSchema.properties.unit.enumNames[index];
      }
    }
    return this.props.inclusionsSchema.properties.unit.enumNames[0];
  };

  render() {
    return (
      <Card variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <ReorderIcon fontSize="small" />
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Min LOS</TableCell>
              <TableCell>Max LOS</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Bonus</TableCell>
              <TableCell>LuxPlus</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell align="right">
                {this.props.onDeleteAll && (
                  <Button
                    color="error"
                    size="small"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={this.props.onDeleteAll}
                  >
                    Delete all
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <DndContext onDragEnd={this.onDragEnd}>
            <SortableContext
              items={this.state.inclusions.map((inclusion: App.RoomRateInclusion) => inclusion.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {this.state.inclusions.map((roomRateInclusion: App.RoomRateInclusion) => {
                  const unit = this.getUnit(roomRateInclusion.unit);
                  return (
                    <RoomRateInclusionSortableItem
                      key={roomRateInclusion.id}
                      roomRateInclusion={roomRateInclusion}
                      unit={unit}
                      onDelete={this.onDelete.bind(this)}
                      onEdit={this.onEdit.bind(this)}
                    />
                  );
                })}
              </TableBody>
            </SortableContext>
          </DndContext>
        </Table>
      </Card>
    );
  }
}
